import { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import { Schedule, QrCode2, AccountBalanceWallet } from '@mui/icons-material';
import StandardCard from '../ui/StandardCard';
import AppSnackbar from '../ui/AppSnackbar';
import useSnackbar from '../../hooks/useSnackbar';
import billingService from '../../services/billingService';
import PixQrCheckout from './PixQrCheckout';
import { softTints, slate } from '../../constants/uiTokens';

// Texto imune ao shrink global de tipografia (<600px) — px do mockup valem no mobile.
const Txt = ({ component = 'div', sx, children }) => (
  <Box component={component} sx={{ m: 0, ...sx }}>{children}</Box>
);

function formatBRL(cents) {
  return (Number(cents || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatValidity(days) {
  if (!days) return 'Sem validade';
  return `${days} dias`;
}

/**
 * ProductCatalog — vitrine reutilizável de pacotes (R2), parametrizada por `meter`
 * (ex.: 'whatsapp_messages'). Compra via PIX embeddado (D-1). Coleta o CPF no 1º uso, guiada
 * pelo backend: tenta sem `document`; se a API responde 400 (CPF obrigatório), abre o modal e
 * refaz com o CPF (a checagem de CPF roda ANTES de criar a intenção → sem order órfã).
 *
 * @param {{ meter: string, balance?: number|null, balanceState?: 'ok'|'low'|'zero' }} props
 */
export default function ProductCatalog({ meter, balance = null, balanceState = 'ok' }) {
  const { snackbar, showError, hideSnackbar } = useSnackbar();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [buyingId, setBuyingId] = useState(null);
  const [checkoutOrder, setCheckoutOrder] = useState(null);

  // Modal de CPF (1º uso)
  const [cpfFor, setCpfFor] = useState(null);   // produto aguardando CPF
  const [cpf, setCpf] = useState('');
  const [cpfSubmitting, setCpfSubmitting] = useState(false);
  const [cpfError, setCpfError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    billingService.listProducts(meter)
      .then((data) => { if (active) { setProducts(Array.isArray(data) ? data : []); setError(null); } })
      .catch(() => { if (active) setError('Não foi possível carregar os pacotes. Tente novamente.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [meter]);

  // Cria a order do pacote no Asaas (D-1). `document` só na 1ª compra sem CPF salvo.
  const startPurchase = useCallback((product, document) => {
    const input = { kind: 'pack', productId: product.id, methods: ['Pix'], providerSlug: 'asaas' };
    if (document) input.document = document;
    return billingService.createOrder(input);
  }, []);

  const handleBuy = useCallback(async (product) => {
    setBuyingId(product.id);
    try {
      const order = await startPurchase(product);
      setCheckoutOrder(order);
    } catch (err) {
      if (err?.response?.status === 400) {
        setCpf('');
        setCpfError(null);
        setCpfFor(product);
      } else {
        showError('Não foi possível iniciar o pagamento. Tente novamente.');
      }
    } finally {
      setBuyingId(null);
    }
  }, [startPurchase, showError]);

  const handleCpfSubmit = useCallback(async () => {
    const digits = cpf.replace(/\D/g, '');
    if (digits.length !== 11) { setCpfError('Digite um CPF válido (11 dígitos).'); return; }
    setCpfSubmitting(true);
    setCpfError(null);
    try {
      const order = await startPurchase(cpfFor, digits);
      setCpfFor(null);
      setCheckoutOrder(order);
    } catch (err) {
      setCpfError(err?.response?.data?.message || 'Não foi possível iniciar o pagamento. Verifique o CPF e tente novamente.');
    } finally {
      setCpfSubmitting(false);
    }
  }, [cpf, cpfFor, startPurchase]);

  const handleCheckoutClose = useCallback(() => setCheckoutOrder(null), []);

  const buyDisabled = !!buyingId || !!checkoutOrder;
  const popularIdx = products.length ? Math.floor(products.length / 2) : -1;
  const saldoTint = softTints[balanceState === 'zero' ? 'error' : balanceState === 'low' ? 'warning' : 'success'];

  return (
    <StandardCard fillHeight={false} sx={{ p: { xs: 2, sm: 2.75 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <AccountBalanceWallet sx={{ fontSize: 22, color: 'primary.dark' }} />
            <Txt sx={{ fontSize: 16, fontWeight: 700, color: 'text.primary' }}>Créditos &amp; Pacotes</Txt>
          </Box>
          <Txt sx={{ fontSize: 13, color: 'text.secondary', mt: 0.75, ml: '34px' }}>
            Pagamento via PIX, liberação na hora. Sem mensalidade — você só paga pelo que usar.
          </Txt>
        </Box>
        {balance != null && (
          <Box sx={{ bgcolor: saldoTint.bg, border: '1px solid', borderColor: saldoTint.border, borderRadius: '10px', px: 2.25, py: 1.25, textAlign: 'right' }}>
            <Txt sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: saldoTint.sub, lineHeight: 1.4 }}>Saldo atual</Txt>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, justifyContent: 'flex-end' }}>
              <Txt component="span" sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1.1, color: saldoTint.fg }}>{Number(balance).toLocaleString('pt-BR')}</Txt>
              <Txt component="span" sx={{ fontSize: 13, color: saldoTint.sub }}>créditos</Txt>
            </Box>
          </Box>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
      ) : products.length === 0 && !error ? (
        <Txt sx={{ fontSize: 14, color: 'text.secondary' }}>Nenhum pacote disponível no momento.</Txt>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.75 }}>
          {products.map((p, i) => {
            const popular = i === popularIdx;
            const perMsg = p.creditQty ? p.priceCents / p.creditQty : 0;
            return (
              <Box
                key={p.id}
                sx={{
                  position: 'relative',
                  flex: '1 1 200px',
                  minWidth: 200,
                  maxWidth: 320,
                  border: popular ? '2px solid' : '1px solid',
                  borderColor: popular ? 'primary.main' : 'divider',
                  bgcolor: popular ? '#f7fbff' : '#fff',
                  borderRadius: '10px',
                  p: 2,
                  pt: popular ? 2.75 : 2,
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {popular && (
                  <Box sx={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', bgcolor: 'primary.main', color: '#fff', fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em', px: 1.5, py: '3px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                    MAIS VENDIDO
                  </Box>
                )}

                <Txt sx={{ fontSize: 24, fontWeight: 700, color: 'text.primary' }}>{p.creditQty}</Txt>
                <Txt sx={{ fontSize: 12, color: slate[500], mb: 1.25 }}>mensagens</Txt>

                <Txt sx={{ fontSize: 22, fontWeight: 700, color: 'primary.dark' }}>{formatBRL(p.priceCents)}</Txt>
                <Txt sx={{ fontSize: 12, color: 'text.secondary', mt: '2px', mb: 1.5 }}>{formatBRL(perMsg)} por mensagem</Txt>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, fontSize: 11.5, color: slate[500], mb: 1.75 }}>
                  <Schedule sx={{ fontSize: 15 }} />Validade {formatValidity(p.validityDays)}
                </Box>

                <Button
                  variant={popular ? 'contained' : 'outlined'}
                  fullWidth
                  disableElevation
                  startIcon={buyingId === p.id ? <CircularProgress size={16} color="inherit" /> : <QrCode2 />}
                  onClick={() => handleBuy(p)}
                  disabled={buyDisabled}
                  sx={{ mt: 'auto', borderRadius: '8px', fontWeight: 600, fontSize: 13.5 }}
                >
                  Comprar via PIX
                </Button>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Modal de CPF (1º uso — D-6) */}
      <Dialog open={!!cpfFor} onClose={() => { if (!cpfSubmitting) setCpfFor(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Digite seu CPF para gerar o QR Code</DialogTitle>
        <DialogContent>
          <Txt sx={{ fontSize: 14, color: 'text.secondary', mb: 2 }}>
            O CPF é exigência do Banco Central para pagamentos via PIX. Guardamos com segurança e
            reutilizamos nas próximas compras.
          </Txt>
          <TextField
            label="CPF" value={cpf} onChange={(e) => setCpf(e.target.value)}
            fullWidth autoFocus inputProps={{ inputMode: 'numeric', maxLength: 14 }}
            error={!!cpfError} helperText={cpfError || 'Somente números'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCpfFor(null)} disabled={cpfSubmitting}>Cancelar</Button>
          <Button
            variant="contained" onClick={handleCpfSubmit} disabled={cpfSubmitting}
            startIcon={cpfSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      {checkoutOrder && (
        <PixQrCheckout order={checkoutOrder} onClose={handleCheckoutClose} />
      )}

      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </StandardCard>
  );
}
